import { createSelector } from 'reselect';
import parseUrl from 'url-parse';
import getSafe from '../utils/getSafe';
import getOwnPath from '../utils/getOwnPath';
import getPlatform from '../utils/getPlatform';
import i18n from '../utils/i18n';
import capitalizeWords from '../utils/capitalizeWords';
import {
  FULFILLMENT_METHOD_DELIVERY,
  FULFILLMENT_METHOD_COLLECTION,
  FULFILLMENT_DAY_DEFAULT,
  FULFILLMENT_TIME_ASAP,
} from '../utils/constants';
import { parseFulfillmentMethod } from '../utils/fulfillmentMethod';
import { parseFulfillmentTime } from '../utils/fulfillmentTime';
import { SUPPORTED_TARGETS } from '../utils/home/constants';
import { CO_CLICK_AND_COLLECT } from '../flags/features';
import { isFeatureActive } from './configSelectors';

export const getSortFilters = ({ home }) => home.feed.sort;
export const getFeedFilters = ({ home }) => home.feed.filters;
export const getCombinedFilters = createSelector(
  [getSortFilters, getFeedFilters],
  (sortFilters, feedFilters) => [...sortFilters, ...feedFilters],
);

export const getDeliveryTime = ({ home }) => home.filters.deliveryTime;
export const showDateTimeModal = ({ home }) => home.filters.showDateTimeModal;
export const showLogisticsModal = ({ home }) => home.filters.showLogisticsModal;
export const getWebUrlParams = ({ home }) => getSafe(() => home.feed.meta.web.url);
export const getResultsCount = ({ home }) => getSafe(() => home.feed.meta.restaurantCount.results);
export const getCollectionLink = ({ home }) => getSafe(() => home.feed.meta.collection.linkTitle);
export const getSearchResults = ({ home }) => getSafe(() => home.feed.searchResults);
export const getSearchQuery = ({ home }) => getSafe(() => home.filters.query);
export const getCollectionParams = ({ home }) =>
  getSafe(() => home.feed.meta.collection.target_params.queryParams);
export const getSubtitle = ({ home }) => getSafe(() => home.feed.results.subheader);
export const getNeighborhood = ({ home }) =>
  getSafe(() => home.feed.meta.location.neighborhoodName) ||
  getSafe(() => capitalizeWords(home.filters.location.neighborhood.replace(/-/g, ' '))) ||
  '';
export const getPostcode = ({ home }) =>
  getSafe(() => home.feed.meta.location.postcode) ||
  getSafe(() => home.filters.location.postcode) ||
  '';
export const getCityName = ({ home }) =>
  getSafe(() => home.feed.meta.location.cityName) ||
  getSafe(() => capitalizeWords(home.filters.location.city.replace(/-/g, ' '))) ||
  '';
export const getSearchId = ({ home }) => home.feed.meta.uuid;
export const getGeohash = (state) => getSafe(() => state.home.filters.location.geohash);
export const getCurrentQuery = (state) => getSafe(() => state.request.currentQuery);
export const getCurrentUrl = (state) => getSafe(() => state.request.currentUrl);

export const getTitle = (state) =>
  getSafe(() => state.home.feed.meta.title) ||
  i18n.gettext('restaurant_list.results.delivery_heading %(location)s', {
    location: getNeighborhood(state),
  });

export const getUrl = (state) => {
  let url = getWebUrlParams(state);
  const isDev = /\.dev$/.test(state.request.host);

  // When in development we can't use the host from the home feed url as it proxies to staging.
  // This block maintains the .dev extension across requests.
  if (isDev) {
    const parsedUrl = parseUrl(url);
    url = `${state.request.host}${parsedUrl.pathname}${parsedUrl.query}`;
  }

  return url;
};

export const buildLocation = ({ home }) => {
  const { geohash, city, neighborhood, postcode } = home.filters.location;

  return {
    geohash,
    city_uname: city,
    neighborhood_uname: neighborhood,
    postcode,
  };
};

/*
  buildSelectedFilters is iterating over an array of objects to find any filters that have been selected and returning the queryParams value for each in a flattened array.
  An example structure of what buildSelectedFilters is iterating over.
    [
      {
        id: 'sort',
        ...
        options: [
          {
            id: 'recommended',
            selected: true,
            target_params: {
              queryParams: 'sort=recommended',
            },
            ...
          },
        ],
      },
      {
        id: 'category',
        ...
        options: [
          {
            id: 'burger',
            selected: false,
            target_params: {
              queryParams: 'category=burger',
            },
            ...
          },
        ],
      }
    ];

  The expected output with the above data would be ['sort=recommended'];
*/

export const buildSelectedFilters = createSelector([getCombinedFilters], (filters) =>
  filters.reduce(
    (selectedParams, control) => [
      ...selectedParams,
      ...control.options.reduce((selected, option) => {
        if (option.selected) {
          return [...selected, option.target_params.queryParams];
        }
        return selected;
      }, []),
    ],
    [],
  ),
);

export const buildDefaultFilters = createSelector([getCombinedFilters], (filters) =>
  filters.reduce(
    (defaultFilters, control) => [
      ...defaultFilters,
      ...control.options.reduce((defaultFilter, option) => {
        if (option.default) {
          return [...defaultFilter, option.target_params.queryParams];
        }
        return defaultFilter;
      }, []),
    ],
    [],
  ),
);

export const buildHomeFeedUrl = createSelector(
  [getCurrentUrl, getCurrentQuery],
  (currentUrl, currentQuery) => {
    const { origin, pathname } = parseUrl(currentUrl);
    const path = origin + pathname;
    const postcode = currentQuery.postcode ? `?postcode=${currentQuery.postcode}` : '';
    const geohash = currentQuery.geohash ? `?geohash=${currentQuery.geohash}` : '';
    return path + (postcode || geohash);
  },
);

// Limiting length of result options array on the client and injecting a view all link
// with target to go to collection view for mvt_1146_co_search_web
// WIP to send this from the backend here https://github.com/deliveroo/co-restaurants/pull/848

export const buildShortSearchResults = createSelector(
  [getSearchResults, getSearchQuery],
  (searchResults, query) =>
    searchResults.map((result) => {
      const shortResults = {
        ...result,
        options: result.options && result.options.slice(0, 5),
        totalResults: result.options && result.options.length,
      };

      const isRestaurantBlock = result.options
        ? result.options.some(
            (option) =>
              getSafe(() => option.target.typeName || false) === SUPPORTED_TARGETS.RESTAURANT,
          )
        : false;

      if (isRestaurantBlock && result.options.length > 5) {
        shortResults.options.push({
          key: 'show-all-results',
          count: result.options.length,
          highlights: [],
          image: {},
          label: i18n.gettext('restaurant_list.results.view_all_button'),
          target: {
            typeName: 'UITargetParams',
            queryParams: `query=${query}`,
            title: query,
          },
        });
      }

      return shortResults;
    }),
);

/**
 * Fulfillment methods selectors / builders
 */
export const buildSupportedFulfillmentMethods = (state) => {
  const withClickAndCollect = isFeatureActive(state, CO_CLICK_AND_COLLECT);
  const fulfillmentMethods = [
    FULFILLMENT_METHOD_DELIVERY,
    withClickAndCollect && FULFILLMENT_METHOD_COLLECTION,
  ];
  return fulfillmentMethods.filter(Boolean);
};
export const getFulfillmentMethod = (state) => {
  const supportedFulfillmentMethods = buildSupportedFulfillmentMethods(state);
  // Try retrieving from BE response first
  const fulfillmentMethod = getOwnPath(
    state,
    ['home', 'feed', 'meta', 'options', 'fulfillmentMethod'],
    // Fallback to value in filters (for the first request)
    getSafe(() => state.home.filters.fulfillmentMethod),
  );
  return parseFulfillmentMethod(supportedFulfillmentMethods, fulfillmentMethod);
};

export const getFulfillmentMethods = ({ home }) =>
  getSafe(() => home.feed.fulfillmentMethods) || [];
export const getShowFulfillmentSwitchControl = (state) => getFulfillmentMethods(state).length > 1;

/**
 * Fulfillment times selectors / builders
 *
 * In Home:
 * - a fulfillment time *always* has at least one day present in the array.
 * - the `asap` object can be `null` if all filtered restaurant are closed
 *
 * In Menu (FYI):
 * - the `days` can be `[]`
 * - `asap` can be `null`
 */
export const getFulfillmentTimeMethods = createSelector(
  [({ home }) => getSafe(() => home.feed.fulfillmentTimes.fulfillmentTimeMethods)],
  (fulfillmentTimeMethods) => fulfillmentTimeMethods || [],
);

export const getHasFulfillmentTimeMethod = createSelector(
  [getFulfillmentTimeMethods],
  (fulfillmentTimeMethods) => fulfillmentTimeMethods.length > 0,
);

export const getFulfillmentTimes = createSelector(
  [getFulfillmentTimeMethods, getFulfillmentMethod],
  (fulfillmentTimeMethods, fulfillmentMethod) => {
    const findFn = (fulfillmentTimeMethod) =>
      fulfillmentTimeMethod.fulfillmentMethod === fulfillmentMethod;
    return fulfillmentTimeMethods.find(findFn) || fulfillmentTimeMethods[0];
  },
);

export const getFulfillmentDays = createSelector(
  [getFulfillmentTimes],
  (fulfillmentTimes) => (fulfillmentTimes ? fulfillmentTimes.days : []),
);

export const buildSupportedFulfillmentTimestamps = createSelector(
  [getFulfillmentDays],
  (fulfillmentDays) => {
    const reduceFn = (supportedFulfillmentTimestamps, day) => {
      day.times.forEach(({ timestamp }) => {
        supportedFulfillmentTimestamps.push(timestamp);
      });
      return supportedFulfillmentTimestamps;
    };

    return fulfillmentDays.reduce(reduceFn, []);
  },
);

export const getIsAsapUnavailable = createSelector(
  [getFulfillmentTimes],
  (fulfillmentTimes) => (fulfillmentTimes ? !fulfillmentTimes.asap : true),
);

export const getFulfillmentTime = (state) => {
  const supportedFulfillmentTimestamps = buildSupportedFulfillmentTimestamps(state);
  const isAsapUnavailable = getIsAsapUnavailable(state);
  const fulfillmentTime =
    // Try retrieving the value from BE response first
    getOwnPath(
      state,
      ['home', 'feed', 'meta', 'options', 'deliveryTime'],
      // Fallback to value in filters (for the first request)
      getSafe(() => state.home.filters.fulfillmentTime),
    );
  return parseFulfillmentTime(supportedFulfillmentTimestamps, fulfillmentTime, isAsapUnavailable);
};

export const getIsAsap = createSelector(
  [getFulfillmentTime],
  (fulfillmentTime) => fulfillmentTime === FULFILLMENT_TIME_ASAP,
);

export const getAsapLabel = createSelector(
  [getFulfillmentTimes],
  (fulfillmentTimes) =>
    getSafe(() => fulfillmentTimes.asap.optionLabel) ||
    i18n.gettext('restaurant_list.time_picker.deliver_now'),
);

export const getFulfillmentDayRef = createSelector(
  [getFulfillmentDays, getFulfillmentTime, getIsAsap],
  (fulfillmentDays, fulfillmentTime, isAsap) => {
    if (isAsap) {
      return fulfillmentDays[0];
    }

    const findDayFn = (day) => day.times.some((time) => time.timestamp === fulfillmentTime);
    return fulfillmentDays.find(findDayFn) || fulfillmentDays[0];
  },
);

export const getFulfillmentTimeRef = createSelector(
  [getFulfillmentTimes, getFulfillmentDayRef, getFulfillmentTime, getIsAsap],
  (fulfillmentTimes, fulfillmentDayRef, fulfillmentTime, isAsap) => {
    if (!fulfillmentTimes || !fulfillmentDayRef) {
      return undefined;
    }

    if (isAsap) {
      return fulfillmentTimes.asap;
    }

    // A match is guaranteed since fulfillmentDayRef would be a ref to
    // the asap object if the fulfillment time was invalid
    const findTimeFn = ({ timestamp }) => timestamp === fulfillmentTime;
    return fulfillmentDayRef.times.find(findTimeFn);
  },
);

export const getFulfillmentDay = createSelector(
  [getFulfillmentDayRef],
  (fulfillmentDayRef) => getSafe(() => fulfillmentDayRef.day) || FULFILLMENT_DAY_DEFAULT,
);
export const getFulfillmentDayLabel = createSelector(
  [getFulfillmentDayRef],
  (fulfillmentDayRef) => getSafe(() => fulfillmentDayRef.dayLabel) || i18n.gettext('today'),
);

export const buildFulfillmentDayOptions = createSelector([getFulfillmentDays], (fulfillmentDays) =>
  fulfillmentDays.map(({ day, dayLabel }) => ({ label: dayLabel, value: day })),
);

export const buildFulfillmentTimeOptionsMap = createSelector(
  [getFulfillmentDays],
  (fulfillmentDays) => {
    const reduceFn = (fulfillmentTimeOptionsMap, { day, times }) => ({
      ...fulfillmentTimeOptionsMap,
      [day]: times.map(({ optionLabel, timestamp }) => ({
        label: optionLabel,
        value: timestamp,
      })),
    });

    return fulfillmentDays.reduce(reduceFn, {});
  },
);

export const getDateTimeLabel = createSelector(
  [getFulfillmentTimeRef, getAsapLabel],
  (fulfillmentTimeRef, asapLabel) =>
    fulfillmentTimeRef ? fulfillmentTimeRef.selectedLabel : asapLabel,
);

export const getIsMobilePlatform = createSelector(
  [({ request }) => getPlatform(request.userAgent)],
  (platform) => ['ios', 'android'].includes(platform),
);
